import React, { useCallback } from 'react';
import { persistQwantum } from 'qwanyx';

/**
 * A utility component that persists data using the persistQwantum function
 * Does not render any visible elements
 * 
 * @param {Object} props
 * @param {string} props._id - The document ID
 * @param {string} props.qwantumId - The property ID within the document
 * @param {any} props.value - The value to persist (optional, can be provided when triggering)
 * @param {boolean} props.backup - Whether to create a backup (optional, defaults to false)
 * @param {Function} props.onPersist - Callback function executed after successful persistence (optional)
 * @param {Function} props.onError - Callback function executed if an error occurs (optional)
 * @param {Function} props.children - Function that receives the persist trigger function
 */
export const PersistQwantum = ({ 
  _id, 
  qwantumId, 
  value: defaultValue, 
  backup = false, 
  onPersist,
  onError,
  children 
}) => {
  // Define the persist function
  const persist = useCallback(async (valueOverride) => {
    try {
      // Use valueOverride if provided, otherwise use the default value from props
      const finalValue = valueOverride !== undefined ? valueOverride : defaultValue;
      
      // Call the persistQwantum function
      await persistQwantum(_id, qwantumId, finalValue, backup);
      
      // Call the onPersist callback if provided
      if (onPersist) {
        onPersist(finalValue);
      }
    } catch (error) {
      // Call the onError callback if provided
      if (onError) {
        onError(error);
      }
    }
  }, [_id, qwantumId, defaultValue, backup, onPersist, onError]);

  // Return null for the component itself (no visible rendering)
  // But pass the persist function to children if it's a function
  return typeof children === 'function' ? children(persist) : null;
};

export default PersistQwantum;